import { Alert, Button, Checkbox, Input, Tooltip } from "antd";
import BaseLayout from "../layouts/BaseLayout";
import { apiRequest, useDatadog } from "@vojomo-core/shared";
import { useEffect, useState } from "react";
import { IoInformationCircleOutline } from "react-icons/io5";

export const VojomoInternal = () => {
  const { trackError } = useDatadog();
  const [googleUser, setGoogleUser] = useState(false);
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(false);

  const [outputText, setOutputText] = useState("");
  const [outputColor, setOutputColor] = useState("");

  const [syncLoading, setSyncLoading] = useState(false);
  const [syncOutputText, setSyncOutputText] = useState("");
  const [syncAlertType, setSyncAlertType] = useState<
    "success" | "info" | "warning" | "error"
  >("success");

  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  const hubspotFunc = async (): Promise<void> => {
    setLoading(true);

    const response: { message: string; color: string } = await apiRequest({
      subRoute: "admin",
      path: `/hubspot_id_to_app?input=${userId}&google_user=${googleUser}`,
      method: "POST",
      responseType: "json"
    });

    setOutputText(response.message);
    setOutputColor(response.color);

    setLoading(false);
  };

  const syncMembersFunc = async (): Promise<void> => {
    setSyncLoading(true);

    const response: { message: string; color: string } = await apiRequest({
      subRoute: "admin",
      path: `/sync_members_to_db`,
      method: "PATCH",
      responseType: "json"
    });

    setSyncOutputText(response.message);
    // Map color to alert type
    if (response.color === "green") {
      setSyncAlertType("success");
    } else if (response.color === "red") {
      setSyncAlertType("error");
    } else if (response.color === "orange") {
      setSyncAlertType("warning");
    } else {
      setSyncAlertType("info");
    }

    setSyncLoading(false);
  };

  const fetchAllUsers = async (): Promise<void> => {
    setUsersLoading(true);
    try {
      const response = await apiRequest({
        subRoute: "admin",
        path: `/get_all_app_users`,
        method: "GET",
        responseType: "json"
      });
      setAllUsers(Array.isArray(response) ? response : []);
    } catch (error) {
      trackError("Error fetching users", error);
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  return (
    <BaseLayout pageTitle="Vojomo Internal">
      <div className="h-[90vh] max-h-[90vh] overflow-y-scroll">
        <h3>Vojomo Internal</h3>

        <div>
          <h4 className="mb-2">Sync Members to Database</h4>
          <Button
            className="font-bold content-center"
            onClick={syncMembersFunc}
            disabled={syncLoading}
            loading={syncLoading}
          >
            Sync Members
          </Button>

          {syncOutputText && (
            <Alert
              message={syncOutputText}
              type={syncAlertType}
              showIcon
              className="mt-4"
            />
          )}
        </div>

        <hr className="my-8" />

        <div>
          <h4 className="mb-2">List of Current Users in the Database</h4>
          {usersLoading ? (
            <p>Loading users...</p>
          ) : (
            <div className="mt-4">
              <p className="mb-4 font-semibold">
                Total Users: {allUsers.length}
              </p>
              {(() => {
                // Group users by company
                const usersByCompany = allUsers.reduce(
                  (acc, user) => {
                    const company = user.company || "No Company";
                    if (!acc[company]) {
                      acc[company] = [];
                    }
                    acc[company].push(user);
                    return acc;
                  },
                  {} as Record<string, any[]>
                );

                // Sort companies alphabetically, but put "No Company" last
                const sortedCompanies = Object.keys(usersByCompany).sort(
                  (a, b) => {
                    if (a === "No Company") return 1;
                    if (b === "No Company") return -1;
                    return a.localeCompare(b);
                  }
                );

                return sortedCompanies.map((company) => (
                  <div key={company} className="mb-6">
                    <h5 className="text-lg mb-2 text-purple-700">
                      {company} ({usersByCompany[company].length})
                    </h5>
                    <ul className="list-disc pl-6 space-y-1">
                      {usersByCompany[company].map((user, index) => (
                        <li key={user._id || index}>
                          <strong>{user.name || "N/A"}</strong> -{" "}
                          {user.email || "N/A"}
                        </li>
                      ))}
                    </ul>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        <hr className="my-8" />

        <div>
          <h4 className="mb-2">Add HubSpot ID to the Vojomo Application</h4>

          <div className="flex flex-col">
            <p className="mb-4 font-bold">HubSpot ID</p>
            <Input
              type="text"
              id="retrieve-hs-id"
              name="input"
              className="mb-4 w-64"
              onChange={(e) => setUserId(e.target.value)}
              value={userId}
              placeholder="Enter HubSpot ID"
            />
            <Checkbox
              // label="Google User"
              className="mb-6"
              checked={googleUser}
              onChange={(e) => setGoogleUser(e.target.checked)}
            >
              Google User
              <Tooltip title="If checked, the user will be added to the Vojomo application as a Google user and won't send a reset password email.">
                <IoInformationCircleOutline className="ml-2" />
              </Tooltip>
            </Checkbox>
          </div>

          <Button
            className="font-bold content-center"
            onClick={hubspotFunc}
            disabled={loading}
            loading={loading}
          >
            Submit
          </Button>

          <p className="font-bold mt-4">
            <span style={{ color: outputColor }}>{outputText}</span>
          </p>
        </div>
      </div>
    </BaseLayout>
  );
};
